/*
 * The organization settings form: what the fields are, what the API stores,
 * and what is dirty.
 *
 * EVERY FIELD HERE MAPS TO A REAL Organization.settings KEY. That is the whole
 * discipline of this file — a control the API does not store is a control that
 * silently does nothing, and this page is the one place an admin expects to be
 * obeyed. Three of html-markup's fields had no backing and are not here:
 *
 *   timezone           Organization has no timezone field. Timezone is per
 *                      USER (User.timezone) and per tracking schedule; there
 *                      is no organization-wide fallback to edit.
 *   trackMultiDisplay  no field, and the desktop captures every attached
 *                      display unconditionally — see the multi-image records
 *                      the Screenshots page flattens.
 *   storage type       local vs S3 is the SERVER's STORAGE_TYPE, not a
 *                      per-organization switch. An org can supply its own
 *                      bucket, which is used inside S3 mode; the effective
 *                      state comes back read-only on the GET.
 *
 * Two names differ from the markup because the model's win: `clipboardEnabled`
 * (not clipboardCapture) and `fileLoggingEnabled` (not fileLogging).
 */

const MINUTE = 60_000;

/* ---- Sections, in page order -------------------------------------------- */
export const SECTIONS = [
    { id: "org", label: "Organization" },
    { id: "tracking", label: "Tracking" },
    { id: "privacy", label: "Privacy" },
    { id: "dashboard", label: "Dashboard" },
    { id: "retention", label: "Data retention" },
    { id: "passwords", label: "Desktop passwords" },
    { id: "storage", label: "Storage" },
];

export const INTERVAL_OPTIONS = [
    ["10000", "10 seconds"],
    ["30000", "30 seconds"],
    ["60000", "1 minute"],
    ["120000", "2 minutes"],
    ["180000", "3 minutes"],
    ["300000", "5 minutes"],
    ["600000", "10 minutes"],
];

export const QUALITY_OPTIONS = [
    ["low", "Low — 854×480"],
    ["medium", "Medium — 1280×720"],
    ["high", "High — 1600×900"],
    ["full", "Full — 1920×1080"],
];

export const TRACKING_TOGGLES = [
    {
        key: "trackKeyboardIntensity",
        label: "Track keyboard intensity",
        hint: "Counts keystrokes per interval. The keys themselves are only recorded if Key Log is on.",
    },
    {
        key: "trackMouseIntensity",
        label: "Track mouse intensity",
        hint: "Counts clicks and pointer distance.",
    },
];

/*
 * `confirm` marks a toggle that asks before being switched ON. All three watch
 * what people type or see, and all three are switched on by one person for
 * everyone else — which is exactly when a confirmation earns its place.
 */
export const PRIVACY_TOGGLES = [
    {
        key: "showTrayIcon",
        label: "Show tray icon",
        hint: "The tracker is visible in the menu bar or system tray.",
    },
    {
        key: "blurScreenshots",
        label: "Blur screenshots",
        hint: "The desktop app blurs the image before upload — the original never leaves the device.",
    },
    {
        key: "trackKeyLog",
        label: "Key log",
        hint: "Records every key pressed. The Key Log tab reconstructs the text from it, so this includes anything typed into a password field.",
        confirm: {
            title: "Turn on key logging?",
            body: "Every key pressed on every tracked device will be recorded beside each screenshot, and the dashboard reconstructs the text from it — including anything typed into a password field. Your team should be told this is on.",
            okLabel: "Turn on key logging",
        },
    },
    {
        key: "clipboardEnabled",
        label: "Clipboard capture",
        hint: "Stores copied and pasted text alongside each screenshot. Images record a size marker only.",
        // Three-tier gate: platform entitlement → this switch → per-user
        // override. The API strips a self-granted enable, so without the
        // entitlement the switch is disabled rather than merely defaulted off.
        gate: "clipboardCaptureAllowed",
        gateNote: "Not enabled for your plan — contact your platform administrator.",
        confirm: {
            title: "Turn on clipboard capture?",
            body: "Text your team copies and pastes on tracked devices will be stored alongside their screenshots. That can include passwords they paste from a manager.",
            okLabel: "Turn on clipboard capture",
        },
    },
    {
        key: "fileLoggingEnabled",
        label: "Diagnostic file logging",
        hint: "Rotating logs on the device for debugging tracking stalls. Never contains key or clipboard content.",
    },
];

export const MODES = [
    {
        key: "regular",
        label: "Regular",
        hint: "The tracker window and tray icon are visible; people can see and pause tracking.",
    },
    {
        key: "silent",
        label: "Silent",
        hint: "Runs without a visible window. Make sure your team is informed — this is a legal requirement in many places.",
        confirm: {
            title: "Switch to silent tracking?",
            body: "The tracker will run with no visible window on every device. In many countries, monitoring staff without telling them is unlawful — make sure your team has been informed.",
            okLabel: "Switch to silent",
        },
    },
];

export const RETENTION_PRESETS = [30, 60, 90, 180, 365];

/*
 * The model's own limits, repeated here so the inputs cannot offer a value the
 * save would reject. html-markup let retention go to 3650 days; the schema caps
 * it at 365, so a ten-year setting would have failed on submit with a
 * validation error and no explanation.
 */
export const LIMITS = {
    retentionMin: 7,
    retentionMax: 365,
    idleMinutesMin: 0.5,
    idleMinutesMax: 60,
};

/* ---- API shape ⇄ form shape --------------------------------------------- */

/**
 * Organization → the form's values.
 *
 * The interval is a string because it is a <select> value; the idle threshold
 * is stored in milliseconds and edited in minutes, and the default (90,000 ms)
 * is a minute and a half — so the input accepts halves rather than rounding
 * the current value away the moment the page loads.
 */
export function toForm(organization = {}) {
    const s = organization.settings ?? {};

    return {
        name: organization.name ?? "",
        screenshotInterval: String(s.screenshotInterval ?? 180000),
        screenshotResolution: s.screenshotResolution ?? "medium",
        idleThresholdMinutes: (s.idleThreshold ?? 90_000) / MINUTE,
        trackKeyboardIntensity: s.trackKeyboardIntensity ?? true,
        trackMouseIntensity: s.trackMouseIntensity ?? true,
        trackMode: s.trackMode ?? "regular",
        blurScreenshots: s.blurScreenshots ?? false,
        showTrayIcon: s.showTrayIcon ?? false,
        trackKeyLog: s.trackKeyLog ?? false,
        clipboardEnabled: s.clipboardEnabled ?? false,
        fileLoggingEnabled: s.fileLoggingEnabled ?? false,
        lateStartTime: s.lateStartTime ?? "09:05",
        dataRetentionDays: s.dataRetentionDays ?? 90,
        trackingInfoPassword: s.trackingInfoPassword ?? "",
        logoutPassword: s.logoutPassword ?? "",
    };
}

const SETTING_KEYS = [
    "screenshotInterval",
    "screenshotResolution",
    "idleThreshold",
    "trackKeyboardIntensity",
    "trackMouseIntensity",
    "trackMode",
    "blurScreenshots",
    "showTrayIcon",
    "trackKeyLog",
    "clipboardEnabled",
    "fileLoggingEnabled",
    "lateStartTime",
    "dataRetentionDays",
    "trackingInfoPassword",
    "logoutPassword",
];

/** One form value → the key and value the API stores. */
function toApi(key, values) {
    if (key === "screenshotInterval") return Number(values.screenshotInterval);
    if (key === "idleThreshold") return Math.round(values.idleThresholdMinutes * MINUTE);
    if (key === "dataRetentionDays") return Number(values.dataRetentionDays);
    return values[key];
}

/**
 * The PATCH body — `{ name?, settings? }` with only what changed.
 *
 * Partial is safe now: updateOrganization writes each key as its own dotted
 * path. It was NOT safe before — `$set: { settings: {…} }` replaced the whole
 * embedded document and deleted every key the request left out, including both
 * desktop gate passwords. See scripts/verify-org-settings-merge.js.
 */
export function toPatch(values, organization) {
    const saved = toForm(organization);
    const savedSettings = organization.settings ?? {};
    const patch = {};
    const settings = {};

    if (values.name !== saved.name) patch.name = values.name;

    for (const key of SETTING_KEYS) {
        const next = toApi(key, values);
        const current =
            key === "idleThreshold" ? (savedSettings.idleThreshold ?? 90_000) : toApi(key, saved);

        if (next !== current) settings[key] = next;
    }

    if (Object.keys(settings).length) patch.settings = settings;
    return patch;
}

/** Human labels for the save bar's "what am I about to change" line. */
export const FIELD_LABELS = {
    name: "Organization name",
    screenshotInterval: "Screenshot interval",
    screenshotResolution: "Screenshot quality",
    idleThreshold: "Idle threshold",
    trackKeyboardIntensity: "Keyboard intensity",
    trackMouseIntensity: "Mouse intensity",
    trackMode: "Tracking mode",
    blurScreenshots: "Blur screenshots",
    showTrayIcon: "Tray icon",
    trackKeyLog: "Key log",
    clipboardEnabled: "Clipboard capture",
    fileLoggingEnabled: "File logging",
    lateStartTime: "Late start cutoff",
    dataRetentionDays: "Data retention",
    trackingInfoPassword: "Tracking info password",
    logoutPassword: "Logout password",
};

export function changedLabels(patch) {
    const keys = [...(patch.name !== undefined ? ["name"] : []), ...Object.keys(patch.settings ?? {})];
    return keys.map((key) => FIELD_LABELS[key] ?? key);
}

/* ---- Derived hints ------------------------------------------------------ */

/** Roughly how many screenshots one person generates in an eight-hour day. */
export function screenshotsPerDay(intervalMs) {
    const ms = Number(intervalMs);
    if (!ms) return 0;
    return Math.round((8 * 3_600_000) / ms);
}

// Approximate JPEG size per preset, from the desktop's capture dimensions.
const QUALITY_KB = { low: 90, medium: 180, high: 280, full: 420 };

/** Storage per person per day, so the two tracking choices show their cost. */
export function dailyStoragePerUser(intervalMs, quality) {
    const shots = screenshotsPerDay(intervalMs);
    const kb = QUALITY_KB[quality] ?? QUALITY_KB.medium;
    return (shots * kb) / 1024; // MB
}
